import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import {
    TabsMenu,
    TabTrigger
} from "@solomon-apps/shared/src/components/ui/tabs-menu";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useMemo, useState } from "react";
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";

import type { DailyEarning } from "@/services/clientPositions";

import { graphColor } from "../../Reporting/graphColors";
import {
    formatLongDate,
    formatPct,
    formatShortDate,
    formatUsd
} from "../../shared/format";
import type { BenchmarkId, BenchmarkSeries } from "../mockData";

/* The "you earned" series intentionally renders in ember (the brand
 * accent) — it's the figure-of-record for this chart. The benchmark
 * overlay reads as olive (graph slot `quinary` = #5F5935) so it sits
 * back from the user series without competing for visual weight. */
const USER_SERIES_COLOR = "rgb(var(--color-accent-base))";
const BENCHMARK_SERIES_COLOR = graphColor("quinary");

export type EarningsRange = "7d" | "30d" | "90d" | "1y" | "all";
export type EarningsView = "usd" | "apy";

interface EarningsChartProps {
    daily: DailyEarning[];
    benchmarks: Record<BenchmarkId, BenchmarkSeries>;
}

interface ChartPoint {
    timestampMs: number;
    userValue: number;
    benchmarkValue: number | null;
}

const RANGE_DAYS: Record<EarningsRange, number | null> = {
    "7d": 7,
    "30d": 30,
    "90d": 90,
    "1y": 365,
    all: null
};

const RANGE_ITEMS: { label: string; value: EarningsRange }[] = [
    { label: "7d", value: "7d" },
    { label: "30d", value: "30d" },
    { label: "90d", value: "90d" },
    { label: "1y", value: "1y" },
    { label: "All", value: "all" }
];

const BENCHMARK_ITEMS: { label: string; value: BenchmarkId }[] = [
    { label: "FDIC Savings", value: "fdic" },
    { label: "3mo T-Bills", value: "tbill" },
    { label: "Aave USDC", value: "aave" }
];

const VIEW_ITEMS: { label: string; value: EarningsView }[] = [
    { label: "USD", value: "usd" },
    { label: "APY", value: "apy" }
];

/** Find the benchmark rate at or before the given timestamp. */
function rateAt(series: BenchmarkSeries, timestampMs: number): number | null {
    if (series.points.length === 0) return null;
    let candidate: number | null = null;
    for (const pt of series.points) {
        if (pt.timestampMs <= timestampMs) candidate = pt.ratePct;
        else break;
    }
    return candidate ?? series.points[0]?.ratePct ?? null;
}

/**
 * Derive chart points for the selected view + range + benchmark.
 * For the $ view, the benchmark line represents "what you would have earned
 * at the benchmark rate against your actual daily-average balance" — i.e.
 * the same principal curve, different rate.
 */
function buildChartPoints(
    daily: DailyEarning[],
    benchmark: BenchmarkSeries,
    view: EarningsView,
    range: EarningsRange
): ChartPoint[] {
    if (daily.length === 0) return [];
    const windowDays = RANGE_DAYS[range];
    const slice =
        windowDays === null
            ? daily
            : daily.slice(Math.max(0, daily.length - windowDays));

    if (view === "apy") {
        return slice.map((d) => ({
            timestampMs: d.timestampMs,
            userValue: d.apyPct,
            benchmarkValue: rateAt(benchmark, d.timestampMs)
        }));
    }

    // $ view — cumulative $ earned, rebased so the window starts at $0.
    // Benchmark cumulative = integrated daily rate * dailyBalance.
    const baseUserCumulative = slice[0]?.cumulativeEarnedUsdv ?? 0;
    let benchCumulative = 0;
    return slice.map((d, i) => {
        if (i > 0) {
            const rate = rateAt(benchmark, d.timestampMs);
            if (rate !== null) {
                benchCumulative += (d.dailyBalanceUsdv * (rate / 100)) / 365;
            }
        }
        return {
            timestampMs: d.timestampMs,
            userValue: d.cumulativeEarnedUsdv - baseUserCumulative,
            benchmarkValue: benchmark.available ? benchCumulative : null
        };
    });
}

export function EarningsChart({
    daily,
    benchmarks
}: EarningsChartProps): React.ReactElement {
    const [range, setRange] = useState<EarningsRange>("30d");
    const [view, setView] = useState<EarningsView>("usd");
    const [benchmarkId, setBenchmarkId] = useState<BenchmarkId>("fdic");

    const activeBenchmark = benchmarks[benchmarkId];
    const benchmarkUnavailable =
        !activeBenchmark.available || activeBenchmark.points.length === 0;

    const chartPoints = useMemo<ChartPoint[]>(
        () => buildChartPoints(daily, activeBenchmark, view, range),
        [daily, activeBenchmark, view, range]
    );

    const hasData = chartPoints.length > 0;
    const valueFormatter =
        view === "apy"
            ? (v: number): string => formatPct(v, 2)
            : (v: number): string => formatUsd(v, { fractionDigits: 0 });

    return (
        <Card
            asElement="section"
            border={false}
            rounded={false}
            aria-label="Earnings over time"
        >
            {/* Header — title + view tab + benchmark picker + range */}
            <div className="flex flex-col gap-3 p-4 border-b border-line-base">
                <div className="flex items-baseline justify-between gap-4">
                    <Heading
                        eyebrow="Earnings over time"
                        title={
                            view === "usd"
                                ? "Cumulative USD Earned"
                                : "Portfolio APY"
                        }
                        titleVariant="h2"
                    />

                    <TabsMenu
                        value={view}
                        onValueChange={(v) => setView(v as EarningsView)}
                    >
                        {VIEW_ITEMS.map((i) => (
                            <TabTrigger key={i.value} value={i.value} size="xs">
                                {i.label}
                            </TabTrigger>
                        ))}
                    </TabsMenu>
                </div>

                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Text variant="label" className="text-foreground-muted">
                            Compare to
                        </Text>
                        <TabsMenu
                            value={benchmarkId}
                            onValueChange={(v) =>
                                setBenchmarkId(v as BenchmarkId)
                            }
                        >
                            {BENCHMARK_ITEMS.map((i) => (
                                <TabTrigger
                                    key={i.value}
                                    value={i.value}
                                    size="xs"
                                >
                                    {i.label}
                                </TabTrigger>
                            ))}
                        </TabsMenu>
                    </div>
                    <TabsMenu
                        value={range}
                        onValueChange={(v) => setRange(v as EarningsRange)}
                    >
                        {RANGE_ITEMS.map((i) => (
                            <TabTrigger key={i.value} value={i.value} size="xs">
                                {i.label}
                            </TabTrigger>
                        ))}
                    </TabsMenu>
                </div>

                {benchmarkUnavailable && (
                    <Text
                        variant="label"
                        render={<div />}
                        className="text-foreground-muted border-t border-line-base pt-2"
                    >
                        Comparison unavailable — showing your earnings only.
                    </Text>
                )}
            </div>

            {/* Chart body */}
            <div className="px-2 pt-4 pb-2">
                {hasData ? (
                    <ResponsiveContainer width="100%" height={300} minWidth={0}>
                        <LineChart
                            data={chartPoints}
                            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                        >
                            <CartesianGrid
                                horizontal
                                vertical={false}
                                stroke="rgb(var(--color-foreground-base) / 0.08)"
                            />
                            <XAxis
                                dataKey="timestampMs"
                                type="number"
                                scale="time"
                                domain={["dataMin", "dataMax"]}
                                tickFormatter={(v: number) =>
                                    formatShortDate(v)
                                }
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fill: "rgb(var(--color-foreground-base) / 0.6)",
                                    fontSize: 11,
                                    fontFamily: "ABC Social"
                                }}
                                minTickGap={48}
                                padding={{ left: 8, right: 8 }}
                            />
                            <YAxis
                                tickFormatter={(v: number) =>
                                    view === "apy"
                                        ? `${v.toFixed(0)}%`
                                        : `$${Math.round(v).toLocaleString()}`
                                }
                                axisLine={false}
                                tickLine={false}
                                width={64}
                                tick={{
                                    fill: "rgb(var(--color-foreground-base) / 0.6)",
                                    fontSize: 11,
                                    fontFamily: "ABC Social"
                                }}
                            />
                            <Tooltip
                                cursor={{
                                    stroke: "rgb(var(--color-foreground-base) / 0.2)"
                                }}
                                content={(props) => {
                                    const p = props as unknown as {
                                        active?: boolean;
                                        payload?: ReadonlyArray<{
                                            payload?: ChartPoint;
                                        }>;
                                    };
                                    return (
                                        <ChartTooltip
                                            active={p.active}
                                            point={p.payload?.[0]?.payload}
                                            view={view}
                                            benchmarkLabel={
                                                activeBenchmark.label
                                            }
                                            valueFormatter={valueFormatter}
                                        />
                                    );
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey="userValue"
                                name="You earned"
                                stroke={USER_SERIES_COLOR}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{
                                    r: 4,
                                    fill: USER_SERIES_COLOR,
                                    stroke: "none"
                                }}
                                isAnimationActive
                                animationDuration={600}
                            />
                            {!benchmarkUnavailable && (
                                <Line
                                    type="monotone"
                                    dataKey="benchmarkValue"
                                    name={activeBenchmark.label}
                                    stroke={BENCHMARK_SERIES_COLOR}
                                    strokeWidth={1.5}
                                    strokeDasharray="4 4"
                                    dot={false}
                                    activeDot={{
                                        r: 3,
                                        fill: BENCHMARK_SERIES_COLOR,
                                        stroke: "none"
                                    }}
                                    isAnimationActive
                                    animationDuration={600}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-[300px] flex items-center justify-center">
                        <Text variant="body" className="text-foreground-muted">
                            Your first distribution will appear here after the
                            next cycle.
                        </Text>
                    </div>
                )}
            </div>

            <LegendRow
                benchmarkLabel={activeBenchmark.label}
                benchmarkUnavailable={benchmarkUnavailable}
            />
        </Card>
    );
}

/* ── Subcomponents ───────────────────────────────────────── */

function LegendRow({
    benchmarkLabel,
    benchmarkUnavailable
}: {
    benchmarkLabel: string;
    benchmarkUnavailable: boolean;
}): React.ReactElement {
    return (
        <Text
            variant="label"
            render={<div />}
            className="px-4 py-3 border-t border-line-base flex items-center gap-5"
        >
            <span className="inline-flex items-center gap-2">
                <span className="h-[2px] w-5 bg-accent-base" />
                <span className="text-foreground-base">You earned</span>
            </span>
            <span
                className={`inline-flex items-center gap-2 ${
                    benchmarkUnavailable
                        ? "text-foreground-imprint"
                        : "text-foreground-muted"
                }`}
            >
                <span
                    className="h-[2px] w-5"
                    style={{
                        backgroundImage: benchmarkUnavailable
                            ? "none"
                            : `linear-gradient(to right, ${BENCHMARK_SERIES_COLOR} 50%, transparent 50%)`,
                        backgroundColor: benchmarkUnavailable
                            ? "rgb(var(--color-foreground-base) / 0.15)"
                            : "transparent",
                        backgroundSize: "8px 2px",
                        backgroundRepeat: "repeat-x"
                    }}
                />
                <span>{benchmarkLabel}</span>
            </span>
            <span className="ml-auto text-foreground-muted">
                Compared against your daily balance in the position.
            </span>
        </Text>
    );
}

function ChartTooltip({
    active,
    point,
    view,
    benchmarkLabel,
    valueFormatter
}: {
    active?: boolean;
    point?: ChartPoint;
    view: EarningsView;
    benchmarkLabel: string;
    valueFormatter: (v: number) => string;
}): React.ReactElement | null {
    if (!active || !point) return null;
    const delta =
        point.benchmarkValue === null
            ? null
            : point.userValue - point.benchmarkValue;
    return (
        <Text
            variant="small"
            render={<div />}
            className="bg-surface-inverse-raised text-foreground-inverse px-3 py-2 shadow-none"
        >
            <Text
                variant="label"
                render={<div />}
                className="text-foreground-inverse-neutral mb-1"
            >
                {formatLongDate(point.timestampMs)}
            </Text>
            <Text
                variant="number"
                render={<div />}
                className="flex items-center justify-between gap-6"
            >
                <span className="text-accent-base">You</span>
                <span>{valueFormatter(point.userValue)}</span>
            </Text>
            {point.benchmarkValue !== null && (
                <>
                    <Text
                        variant="number"
                        render={<div />}
                        className="flex items-center justify-between gap-6"
                    >
                        <span className="text-foreground-inverse-muted">
                            {benchmarkLabel}
                        </span>
                        <span>{valueFormatter(point.benchmarkValue)}</span>
                    </Text>
                    {delta !== null && (
                        <Text
                            variant="number"
                            render={<div />}
                            className="mt-1 pt-1 border-t border-line-inverse-muted flex items-center justify-between gap-6"
                        >
                            <Text
                                variant="label"
                                className="text-foreground-inverse-neutral"
                            >
                                Delta
                            </Text>
                            <span
                                className={
                                    delta >= 0
                                        ? "text-accent-base"
                                        : "text-foreground-inverse"
                                }
                            >
                                {view === "apy"
                                    ? `${delta >= 0 ? "+" : "−"}${Math.abs(
                                          delta
                                      ).toFixed(2)}%`
                                    : `${
                                          delta >= 0 ? "+" : "−"
                                      }${valueFormatter(
                                          Math.abs(delta)
                                      ).replace(/^-/, "")}`}
                            </span>
                        </Text>
                    )}
                </>
            )}
        </Text>
    );
}
